import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { 
  Mail, Users, Plus, Trash2, Search, ArrowLeft,
  ChevronRight, UserPlus, UserMinus, ListPlus
} from 'lucide-react';
import Navbar from '../components/Navbar';
import api, { mailingListsAPI } from '../lib/api';

const MailingLists = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedList, setSelectedList] = useState(null);
  const [listStudents, setListStudents] = useState([]);
  const [showAddList, setShowAddList] = useState(false);
  const [showAddStudents, setShowAddStudents] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSyncFromClasses = async () => {
    if (!confirm('Auto-tag every student into the class-specific mailing lists based on their bookings? This will ADD missing tags and REMOVE tags for students who no longer have a matching class. Custom tags are not touched.')) return;
    setSyncing(true);
    try {
      const res = await api.post('/mailing-lists/sync-from-classes');
      const { total_added = 0, total_removed = 0 } = res.data || {};
      toast.success(`Class lists synced — ${total_added} added, ${total_removed} removed`);
      fetchLists();
      if (selectedList) await fetchListStudents(selectedList.name);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchLists();
    fetchStudents();
  }, []);

  const fetchLists = async () => {
    try {
      const res = await mailingListsAPI.getAll();
      setLists(res.data.lists || []);
    } catch (error) {
      toast.error('Failed to load mailing lists');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await api.get('/students/all');
      setStudents(res.data.students || []);
    } catch (error) {
      console.error('Failed to load students:', error);
    }
  };

  const fetchListStudents = async (listName) => {
    try {
      const res = await mailingListsAPI.getStudents(listName);
      setListStudents(res.data.students || []);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  const handleSelectList = async (list) => {
    setSelectedList(list);
    await fetchListStudents(list.name);
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.error('Please enter a list name');
      return;
    }
    setProcessing(true);
    try {
      await mailingListsAPI.create({ name: newListName, description: newListDescription });
      toast.success('Mailing list created');
      setShowAddList(false);
      setNewListName('');
      setNewListDescription('');
      fetchLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create list');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteList = async (listName) => {
    if (!confirm(`Delete mailing list "${listName}"? Students will be removed from this list.`)) return;
    try {
      await mailingListsAPI.delete(listName);
      toast.success('Mailing list deleted');
      setSelectedList(null);
      fetchLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete list');
    }  };

  const handleAddStudentsToList = async () => {
    if (selectedStudentIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    setProcessing(true);
    try {
      await mailingListsAPI.addStudents(selectedList.name, selectedStudentIds);
      toast.success(`Added ${selectedStudentIds.length} students to "${selectedList.name}"`);
      setShowAddStudents(false);
      setSelectedStudentIds([]);
      fetchLists();
      fetchListStudents(selectedList.name);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add students');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await mailingListsAPI.removeStudents(selectedList.name, [studentId]);
      toast.success('Student removed from list');
      fetchLists();
      fetchListStudents(selectedList.name);
    } catch (error) {
      toast.error('Failed to remove student');
    }
  };

  const toggleStudentSelection = (id) => {
    setSelectedStudentIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Filter students not already in the selected list
  const availableStudents = students.filter(
    s => !listStudents.some(ls => ls.id === s.id)
  ).filter(
    s => s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         s.parent?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#0a0a0a]'}>
      {!embedded && <Navbar />}
      <Toaster position="top-right" richColors />
      
      <main className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-8'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {!embedded && (
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-white/70 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSyncFromClasses}
              disabled={syncing}
              variant="outline"
              className="border-[#333] text-white hover:bg-white/5"
              data-testid="sync-class-mailing-lists-btn"
              title="Auto-tag students into class-specific mailing lists based on their bookings"
            >
              <ListPlus className="w-4 h-4 mr-2" />
              {syncing ? 'Syncing…' : 'Sync from classes'}
            </Button>
            <Button
              onClick={() => setShowAddList(true)}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Mailing List
            </Button>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
          <Mail className="w-8 h-8 text-purple-400" />
          Mailing Lists
        </h1>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Lists Panel */}
          <Card className="bg-[#111] border-[#333] md:col-span-1">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ListPlus className="w-5 h-5 text-purple-400" />
                Lists ({lists.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto" />
                </div>
              ) : lists.length === 0 ? (
                <div className="p-8 text-center text-white/50">
                  No mailing lists yet
                </div>
              ) : (
                <div className="divide-y divide-[#333]">
                  {lists.map(list => (
                    <div
                      key={list.name}
                      onClick={() => handleSelectList(list)}
                      className={`p-4 cursor-pointer transition-all flex items-center justify-between ${
                        selectedList?.name === list.name 
                          ? 'bg-purple-500/20 border-l-4 border-purple-500' 
                          : 'hover:bg-white/5'
                      }`}
                      data-testid={`mailing-list-${list.name}`}
                    >
                      <div>
                        <p className="text-white font-medium">{list.name}</p>
                        <p className="text-white/50 text-sm">{list.student_count} students</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {list.is_default && (
                          <Badge className="bg-blue-500/20 text-blue-400 text-xs">Default</Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-white/30" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* List Details Panel */}
          <Card className="bg-[#111] border-[#333] md:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                {selectedList ? selectedList.name : 'Select a List'}
              </CardTitle>
              {selectedList && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => setShowAddStudents(true)}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Students
                  </Button>
                  {!selectedList.is_default && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteList(selectedList.name)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!selectedList ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Select a mailing list to view students</p>
                </div>
              ) : listStudents.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No students in this list</p>
                  <Button
                    size="sm"
                    onClick={() => setShowAddStudents(true)}
                    className="mt-4 bg-purple-500 hover:bg-purple-600"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Students
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {listStudents.map(student => (
                    <div
                      key={student.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-[#1a1a1a] border border-[#333]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <span className="text-purple-400 font-semibold text-sm">
                            {student.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{student.name}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveStudent(student.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Create List Dialog */}
        <Dialog open={showAddList} onOpenChange={setShowAddList}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Mailing List</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-white/70 mb-1 block">List Name *</label>
                <Input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="e.g., Summer Camp 2026"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Description (optional)</label>
                <Input
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="A brief description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddList(false)} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={handleCreateList} disabled={processing} className="bg-[#FF007F] hover:bg-[#D6006A]">
                {processing ? 'Creating...' : 'Create List'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Students Dialog */}
        <Dialog open={showAddStudents} onOpenChange={setShowAddStudents}>
          <DialogContent className="bg-[#111] border-[#333] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Add Students to "{selectedList?.name}"</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#1a1a1a] border-[#333] text-white"
                />
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableStudents.length === 0 ? (
                  <p className="text-white/50 text-center py-4">
                    {searchQuery ? 'No matching students' : 'All students are already in this list'}
                  </p>
                ) : (
                  availableStudents.map(student => {
                    const isSelected = selectedStudentIds.includes(student.id);
                    return (
                      <div
                        key={student.id}
                        onClick={() => toggleStudentSelection(student.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-purple-500/20 border-purple-500'
                            : 'bg-[#1a1a1a] border-[#333] hover:border-purple-500/50'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleStudentSelection(student.id)}
                          className="border-white/30 data-[state=checked]:bg-purple-500"
                        />
                        <div className="flex-1">
                          <p className="text-white font-medium">{student.name}</p>
                          <p className="text-white/50 text-xs">{student.parent?.email}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              {selectedStudentIds.length > 0 && (
                <p className="text-white/70 text-sm mt-4">
                  {selectedStudentIds.length} student(s) selected
                </p>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => { setShowAddStudents(false); setSelectedStudentIds([]); setSearchQuery(''); }} 
                className="border-[#333] text-white"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddStudentsToList} 
                disabled={processing || selectedStudentIds.length === 0} 
                className="bg-purple-500 hover:bg-purple-600"
              >
                {processing ? 'Adding...' : `Add ${selectedStudentIds.length} Student(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default MailingLists;
